import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import { ArrowRight, Briefcase, Fingerprint, Sparkles, Users, Sparkles as Spark } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { SiteBreadcrumbs } from "@/components/marketing/SiteBreadcrumbs";
import heroImg from "@/assets/hero-card.webp";

export const Route = createFileRoute("/platform/")({
  head: () => ({
    meta: [
      { title: "Platform — banana.cards" },
      { name: "description", content: "The full Banana platform: digital identity & sharing, an AI suite, a built-in CRM, and the everyday business apps that turn taps into revenue." },
      { property: "og:title", content: "Platform — banana.cards" },
      { property: "og:description", content: "Identity & sharing, AI, CRM, and run-your-business apps — one platform that grows with you." },
      { property: "og:image", content: heroImg },
      { property: "og:url", content: "https://banana.cards/platform" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/platform" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "The Banana Platform",
          description: "The full Banana platform: digital identity & sharing, an AI suite, a built-in CRM, and the everyday business apps that turn taps into revenue.",
          url: "https://banana.cards/platform",
          isPartOf: { "@type": "WebSite", name: "banana.cards", url: "https://banana.cards" },
        }),
      },
    ],
  }),
  component: Page,
});

type Pillar = {
  title: string;
  href: string;
  icon: LucideIcon;
  desc: string;
  links: { label: string; href: string }[];
};

const pillars: Pillar[] = [
  {
    title: "Identity & Sharing",
    href: "/platform/identity-and-sharing",
    icon: Fingerprint,
    desc: "Your digital business card, premium NFC products, and every share channel — QR, Wallet, links, email signatures.",
    links: [
      { label: "Digital business cards", href: "/platform/cards" },
      { label: "NFC products", href: "/platform/nfc" },
      { label: "Networking & sharing", href: "/platform/networking" },
    ],
  },
  {
    title: "AI Suite",
    href: "/platform/ai-suite",
    icon: Sparkles,
    desc: "An AI brain that remembers every contact, plus enrichment and tools that write, translate and score for you.",
    links: [
      { label: "AI Brain memory", href: "/platform/ai" },
      { label: "Auto-enrichment", href: "/platform/ai-enrichment" },
      { label: "Writer, translator, scorer", href: "/platform/ai-tools" },
    ],
  },
  {
    title: "CRM",
    href: "/platform/crm",
    icon: Users,
    desc: "Capture every lead, move deals through smart pipelines, and see what's working in real time.",
    links: [
      { label: "Contacts & lead capture", href: "/platform/contacts" },
      { label: "Pipelines & tasks", href: "/platform/pipelines" },
      { label: "Real-time analytics", href: "/platform/analytics" },
    ],
  },
  {
    title: "Run your business",
    href: "/platform/run-your-business",
    icon: Briefcase,
    desc: "Bookings, meetings, contracts, automations, reviews, events, tasks — installed in one click.",
    links: [
      { label: "Bookings & meetings", href: "/platform/bookings" },
      { label: "Contracts & e-sign", href: "/platform/contracts" },
      { label: "Automations & reviews", href: "/platform/automations" },
    ],
  },
];

function Page() {
  return (
    <SiteLayout>
      {/* HERO */}
      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-12 pt-20 md:grid-cols-2 md:px-6">
          <div className="flex flex-col justify-center order-2 md:order-1">
            <SiteBreadcrumbs className="mb-6" currentName="The Banana Platform" />
            <h1 className="font-display text-3xl leading-[1.1] md:text-5xl">
              Four product suites, <em className="italic">one operating system for your network.</em>
            </h1>
            <p className="mt-5 max-w-md text-base text-muted-foreground md:text-lg">
              Banana is built on four pillars: your identity & how you share it, an AI brain that remembers every contact, a CRM that turns conversations into pipeline, and the apps you run your business on.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]">
                Get started — free <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link to="/pricing" className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/70">
                See pricing
              </Link>
            </div>
          </div>
          <div className="relative order-1 md:order-2">
            <div className="absolute -inset-6 rounded-[2rem] bg-brand-lime/10 blur-3xl" aria-hidden />
            <img src={heroImg} alt="The Banana platform: NFC business cards, CRM, bookings, contracts and AI tools working together for founders and sales teams" width={1600} height={1024} className="relative w-full rounded-2xl object-cover" />
          </div>
        </div>
      </section>

      {/* PILLARS GRID */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">What's inside</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Everything you need, <em className="italic">in one place.</em>
            </h2>
            <p className="mt-4 text-muted-foreground">
              Click any pillar title for the full overview — or jump straight to a specific tool.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {pillars.map((p) => {
              const Icon = p.icon;
              return (
                <article key={p.href} className="flex flex-col rounded-2xl bg-card p-7 md:p-8">
                  <div className="flex items-start justify-between gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
                      <Icon className="h-5 w-5" />
                    </span>
                    <Link
                      to={p.href}
                      className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1.5 text-xs font-bold text-foreground transition hover:bg-foreground hover:text-background"
                      aria-label={`Open ${p.title} overview`}
                    >
                      Overview <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>

                  <h3 className="mt-6 font-display text-2xl leading-tight md:text-3xl">
                    <Link to={p.href} className="underline-offset-4 hover:underline">
                      {p.title}
                    </Link>
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-base">{p.desc}</p>

                  <div className="mt-6 border-t border-muted-foreground/10 pt-5">
                    <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                      Jump straight to
                    </p>
                    <ul className="grid gap-2">
                      {p.links.map((l) => (
                        <li key={l.href}>
                          <Link
                            to={l.href}
                            className="group flex items-center justify-between gap-3 rounded-xl bg-muted px-4 py-3 text-sm font-bold transition hover:bg-foreground hover:text-background"
                          >
                            <span>{l.label}</span>
                            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUTRO */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-20 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_50%)]" aria-hidden />
            <Spark className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              One platform. <em className="italic text-brand-lime">Every step of the relationship.</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
              From the moment you tap to share your card to the day you close the deal — Banana keeps the context.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
                Get started — free
              </CtaLink>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
                Talk to sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
